<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="sm:max-w-lg md:max-w-2xl rounded-lg bg-white shadow-xl p-0 gap-0 max-h-[90vh]" @pointer-down-outside.prevent @interact-outside.prevent>
      <DialogHeader class="flex flex-row items-center justify-between py-4 px-5 border-b border-gray-400">
        <DialogTitle class="text-xl font-medium text-gray-900">
          {{ fullTitle }}
        </DialogTitle>
        <DialogDescription class="sr-only">
          Review {{ effectiveDescriptionLabel }} according to the criteria listed below.
        </DialogDescription>
      </DialogHeader>

      <div class="overflow-y-auto max-h-[70vh]">
        <template v-if="hasVisibleCriteria">
          <template v-for="section in visibleSections" :key="section.type">
            <div v-if="section.subtitle" class="bg-gray-200 border-b border-gray-400 px-5 py-3 text-gray-900">
              {{ section.subtitle }}
            </div>
            <ul>
              <li
                v-for="item in section.items"
                :key="item.label"
                class="px-5 py-4 border-b border-gray-400 last:border-b-0"
              >
                <div class="flex items-start justify-between gap-4">
                  <p class="font-medium text-gray-900">{{ item.label }}</p>
                  <span class="text-md text-gray-700 shrink-0 mt-0.5">Required</span>
                </div>
                <p class="text-md text-gray-700 mt-1">{{ item.description }}</p>
              </li>
            </ul>
          </template>
        </template>
        <p v-else class="px-5 py-4 text-gray-700 italic">
          No review criteria available for the current profile.
        </p>
      </div>

      <DialogFooter class="px-5 py-4 border-t border-gray-400 flex gap-3 justify-end">
        <GbButton gbTheme="grayOutlineSecondary" @click="$emit('update:open', false)">
          Cancel
        </GbButton>
        <GbButton
          gbTheme="aiPrimary"
          :disabled="!hasVisibleCriteria"
          @click="$emit('review')"
        >
          <span class="material-icons-outlined text-base mr-1.5" aria-hidden="true">assignment_turned_in</span>
          {{ effectiveReviewButtonText }}
        </GbButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/dialog'
import GbButton from '@/components/GbButton.vue'
import type { CriteriaItem } from '@/content-review/api'
import { isCriterionForSubjects } from '@/content-review/utils/criteria'

// Subtitle strings owned here, keyed by content type — callers just pass the type key.
const SECTION_SUBTITLES: Record<string, string> = {
  presentlevels: 'Review present levels according to the following criteria set:',
  impact: 'Review impact of disability according to the following criteria set:',
  goals: 'Review annual goal according to the following criteria set:',
  objectives: 'Review objectives according to the following criteria set:',
}

const props = defineProps<{
  open: boolean
  /** Multi-section callers (e.g. PLW): pass raw criteria keyed by content type. */
  criteriaByType?: Record<string, CriteriaItem[]>
  /** Single-section callers (IOD): pass a flat list; wrapped into one unlabeled section. */
  criteria?: CriteriaItem[]
  criteriaSetName?: string
  subjects: string[]
  contentLabel?: string
  descriptionLabel?: string
  reviewButtonText?: string
  /** Overrides the default subject match, e.g. goals also match criteria on grade. */
  isCriterionVisible?: (item: CriteriaItem) => boolean
}>()

defineEmits<{
  'update:open': [value: boolean]
  review: []
}>()

const effectiveDescriptionLabel = computed(() =>
  props.descriptionLabel || (props.contentLabel ? props.contentLabel.toLowerCase() : 'statement')
)

const effectiveReviewButtonText = computed(() =>
  props.reviewButtonText || `Review ${props.contentLabel || 'Statement'}`
)

const isVisible = computed(
  () => props.isCriterionVisible ?? ((item: CriteriaItem) => isCriterionForSubjects(item, props.subjects))
)

const fullTitle = computed(() => {
  const base = props.contentLabel ? `${props.contentLabel} Auto-review` : 'Auto-review'
  return props.criteriaSetName ? `${base} (${props.criteriaSetName})` : base
})

// Normalise into a uniform list of { type, subtitle, items } sections.
const visibleSections = computed(() => {
  // Multi-section path: criteriaByType keyed by content type
  if (props.criteriaByType && Object.keys(props.criteriaByType).length > 0) {
    return Object.entries(props.criteriaByType)
      .map(([type, criteria]) => ({
        type,
        subtitle: SECTION_SUBTITLES[type] ?? '',
        items: criteria.filter(isVisible.value),
      }))
      .filter((section) => section.items.length > 0)
  }

  // Single-section path: flat criteria list, subtitle matches the old hardcoded string
  const subtitle = `Review ${effectiveDescriptionLabel.value} according to the following criteria set:`
  const items = (props.criteria ?? []).filter(isVisible.value)
  return items.length > 0 ? [{ type: 'default', subtitle, items }] : []
})

const hasVisibleCriteria = computed(() => visibleSections.value.length > 0)
</script>
